import React from 'react';
import axios from 'axios';
import {
	getSession,
} from 'Vulture/Session';
import {
	broncoURL,
	commitHash,
} from 'Vulture/ENV';
import CustomIcon from '../Common/CustomIcon/CustomIcon';
import style from './Chonky/ChonkyFileBrowser.module.css';
import {
	pollAIGenerationEventsByGalleryFile,
} from 'Vulture/AIGeneration';

class EditFileModal extends React.Component {
	constructor(props) {
		super(props);
		const {
			file,
		} = props;

		this.state = {
			fileName:    file?.file_name || file?.name || '',
			description: file?.description || '',
			keywords:    file?.keywords || '',
			resolution:  file?.resolution || '',
			order:       file?.order || 1,
			analysisStatus: null,
		};

		this.pollInterval = null;

		this.handleInputChange = this.handleInputChange.bind(this);
		this.handleEditFile = this.handleEditFile.bind(this);
	}

	componentDidMount() {
		// Poll only while this file can actually have a generation in flight:
		// keywords are the only AI-filled field, so a populated value means
		// nothing is pending and no request is needed. The first poll settles
		// the real status (finished/failed/none → fetchAnalysisStatus stops
		// the interval); polling always ends when the modal unmounts.
		if (!this.state.keywords) {
			this.fetchAnalysisStatus();
			this.pollInterval = setInterval(this.fetchAnalysisStatus, 15000);
		}
	}

	componentWillUnmount() {
		this.unmounted = true;
		this.stopPolling();
	}

	stopPolling = () => {
		if (this.pollInterval) {
			clearInterval(this.pollInterval);
			this.pollInterval = null;
		}
	};

	// Poll this file's AI analysis status so the queued indicator updates live
	// while the modal is open. Stops once there's nothing left to watch.
	fetchAnalysisStatus = async () => {
		const {
			file,
		} = this.props;
		if (!file || !file.id) {
			return;
		}

		const backendFileId = file.id.replace('file_', '');
		try {
			const data = await pollAIGenerationEventsByGalleryFile(backendFileId);
			if (this.unmounted) {
				return;
			}
			const events = Array.isArray(data?.events) ? data.events : [];
			const event = events.length > 0 ? events[0] : null;
			const status = event ? event.status : null;
			this.setState({ analysisStatus: status });

			// Nothing in flight — stop polling.
			const isPending = status === 'pending' || status === 'processed';
			if (!isPending) {
				this.stopPolling();
				// A finished generation wrote keywords to the file row, but the
				// browser's fileMap still holds the pre-generation snapshot —
				// refresh it (silently: no loading overlay behind the modal) and
				// seed the still-empty field from the authoritative fresh row.
				// The row disambiguates: empty keywords there means the user
				// cleared them, so nothing deleted is ever resurrected.
				if (status === 'finished' && typeof this.props.refreshFileBrowserData === 'function') {
					const freshMap = await this.props.refreshFileBrowserData({ silent: true });
					const fresh = freshMap && this.props.file ? freshMap[this.props.file.id] : null;
					if (fresh && fresh.keywords && !this.unmounted) {
						this.setState((prev) => (prev.keywords ? null : { keywords: fresh.keywords }));
					}
				}
			}
		} catch (err) {
			console.error('File AI analysis poll error:', err);
		}
	};

	handleInputChange(e) {
		this.setState({
			[e.target.name]: e.target.value,
		});
	}

	async handleEditFile() {
		const {
			fileName,
			description,
			keywords,
			order,
		} = this.state;

		const {
			file,
			refreshFileBrowserData,
			app,
			accountUUID,
		} = this.props;

		app.setState({
			loading: true,
		});

		app.load(async () => {
			try {
				const session = getSession();
				const backendFileId = file.id.replace('file_', '');

				const response = await axios.put(`${broncoURL}/api/file/${backendFileId}?account_uuid=${accountUUID}`, {
					file_name: fileName,
					description,
					keywords,
					order,
				}, {
					headers: {
						'X-Auth-Session':      session.session,
						'X-Spark-API-version': commitHash,
					},
				});

				if (response.status === 200) {
					await refreshFileBrowserData();
					app.closeModal();
					this.props.refreshLibraryView();
				}
			} catch (err) {
				console.error('File update error:', err);
				alert(`Error updating file: ${err.response?.data?.message || err.message}`);
			}
		});
	}

	// Indicator shown left of a field label when an AI analysis is queued/in
	// flight for this file and the field is still empty (so it will be filled).
	renderQueuedIndicator() {
		return (
			<span className={style.aiIndicator}>
				<span className={style.queuedLoader}>
					<span className="loader loader-bars"><span /></span>
				</span>
				<span className={style.settingHelpToolTip}>{`Queued for AI generation — this field will be populated.`}</span>
			</span>
		);
	}

	render() {
		const {
			isVCLEnabled,
		} = this.props;

		const {
			fileName,
			description,
			keywords,
			resolution,
			order,
			analysisStatus,
		} = this.state;

		const analysisPending = analysisStatus === 'pending' || analysisStatus === 'processed';

		return (
			<div className={style.chonkyModal}>
				<div className={style.formGroup}>
					<label>
						File Name
						<input
							type="text"
							name="fileName"
							value={fileName}
							disabled
						/>
					</label>
				</div>

				<div className={style.formGroup}>
					<label>
						<span className={style.labelWithHelp}>
							Descriptive Name
							<span className={style.settingHelpIcon}>
								<CustomIcon icon='question-circle' />
								<span className={style.settingHelpToolTip}>{`Descriptive name for identification.`}</span>
							</span>
						</span>
						<input
							type="text"
							name="description"
							value={description}
							onChange={this.handleInputChange}
						/>
					</label>
				</div>

				<div className={style.formGroup}>
					<label>
						<span className={style.labelWithHelp}>
							{analysisPending && !keywords && this.renderQueuedIndicator()}
							Keywords
							<span className={style.settingHelpIcon}>
								<CustomIcon icon='question-circle' />
								<span className={style.settingHelpToolTip}>{`Comma-separated keywords used for asset search.`}</span>
							</span>
						</span>
						<input
							type="text"
							name="keywords"
							value={keywords}
							onChange={this.handleInputChange}
							placeholder="Comma-separated keywords"
						/>
					</label>
				</div>

				<div className={style.formGroup}>
					<label>
						<span className={style.labelWithHelp}>
							Resolution
							<span className={style.settingHelpIcon}>
								<CustomIcon icon='question-circle' />
								<span className={style.settingHelpToolTip}>{`Width × height detected on upload. Read-only.`}</span>
							</span>
						</span>
						<input
							type="text"
							name="resolution"
							value={resolution}
							disabled
						/>
					</label>
				</div>

				{isVCLEnabled && <div className={`text-small ${style.order}`}>
					<div className={`label ${style.label}`}>
						<label>
							<span className={style.labelWithHelp}>
								Order
								<span className={style.settingHelpIcon}>
									<CustomIcon icon='question-circle' />
									<span className={style.settingHelpToolTip}>{`Display order in visual content loops. Lower numbers appear first.`}</span>
								</span>
							</span>
						</label>
					</div>
					<input
						value={order}
						id='modalOrder'
						type='number'
						placeholder='1'
						onChange={(e) => {
							this.setState({
								order: Number(e.target.value.trim()),
							});
						}}
						onBlur={(e) => {
							this.setState({
								order: Number(e.target.value.trim()),
							});
						}}
					/>
				</div>}

				<button
					onClick={this.handleEditFile}
					className={`button`}
				>
					<CustomIcon icon='save' /> Save Changes
				</button>
			</div>
		);
	}
}

export default EditFileModal;